// Copyright: IQGeo Limited 2010-2019
import $ from 'jquery';
import _ from 'underscore';
import myw from 'myWorld/base/core';
import featureViewerHtml from 'text!html/featureViewer.html';
import 'myWorld/base/control';
import './fieldViewer';
import './referenceFieldViewer';
import './referenceSetFieldViewer';
import './referenceTooltipFieldViewer';
import './referenceSetTooltipFieldViewer';
import './timeFieldViewer';
import './linkFieldViewer';
import './imageFieldViewer';
import './htmlFieldViewer';
import './fileFieldViewer';
import './numberFieldViewer';

//get the templates from the html file
const elems = $(featureViewerHtml),
    attrListHtml = elems.filter('#attributes-list-template').html();

myw.FeatureViewer = myw.Control.extend({
    template: _.template(featureViewerHtml),
    attributeListTemplate: _.template(attrListHtml),

    options: {
        state: {
            //state is shared across instances (this object is instantiated only when the class is defined)
            attributeDisplayMode: 'medium',
            collapsed: false
        }
    },

    events: {
        'click .panel-header': 'toggleDetails',
        'click .toggle-defaults.collapse': 'hideDefaultAttributes',
        'click .toggle-defaults:not(".collapse")': 'showDefaultAttributes'
    },

    fieldViewerMapping: {
        reference: myw.ReferenceFieldViewer,
        reference_set: myw.ReferenceSetFieldViewer,
        foreign_key: myw.ReferenceFieldViewer,
        timestamp: myw.TimeFieldViewer,
        integer: myw.NumberFieldViewer,
        double: myw.NumberFieldViewer,
        numeric: myw.NumberFieldViewer,
        link: myw.LinkFieldViewer,
        image: myw.ImageFieldViewer,
        file: myw.FileFieldViewer,
        default: myw.FieldViewer
    },

    /**
     * @class A UI control to visualize a feature's details <br/>
     *        Used by DetailsControl. <br/>
     *        Displaying of feature details is customizable by registering plugins with the pluginIds parameter. See {@link DetailsControl} <br/>
     *        Another way to customize behaviour is by creating a subclass and using it as the
     *        'featureViewer' in the corresponding feature model class. <br/>
     *        Field values are displayed using instances of {@link FieldViewer}
     * @param  {Application|Plugin} owner The component which is the owner of self.
     *                        Can be a FeatureDetailsControl (standard app) or HandheldApp
     * @constructs
     * @extends {Control}
     */
    initialize(owner, options) {
        myw.Control.prototype.initialize.call(this, owner, options);

        this.app = owner.app;

        this.plugins = owner.plugins || {};

        //this object is shared across differente instances so that we get consistent behaviour
        //when using custom viewers
        this.state = this.options.state;

        this.app.on('featureCollection-modified', ev => {
            const urn = this.feature && this.feature.getUrn();
            if (ev.feature && ev.feature.getUrn() == urn) {
                this.displayFeatureDetails(ev.feature); //refresh
            }
        });
    },

    hideDefaultAttributes(e) {
        e.stopPropagation();
        this.state.attributeDisplayMode = 'medium';
        this.render();
        this.toggleDefaultsBtn.removeClass('collapse').attr('title', this.msg('show-all'));
    },

    showDefaultAttributes(e) {
        e.stopPropagation();
        this.state.attributeDisplayMode = 'full';
        this.render();
        this.toggleDefaultsBtn.addClass('collapse').attr('title', this.msg('hide-defaults'));
    },

    /**
     * Display feature details:
     * title, link for access to other selection features, feature properties and other associated controls
     * @param  {Feature}    feature     Feature to display
     * @param  {featureEvent}   e           Original event details - to pass on to plugins
     */
    displayFeatureDetails(feature, e) {
        this.feature = feature;
        this.featureEvent = e;

        return this.render();
    },

    /**
     * Renders the feature's title and attributes
     */
    render() {
        const feature = this.feature;
        const myw_delta_description = feature.isDeltaSchema() && feature.getDeltaDescription();
        const templateValues = _.extend({}, feature.properties, {
            myw_title: feature.getTitle(),
            myw_short_description: feature.getShortDescription(),
            myw_delta_description,
            masterView: feature.datasource.options.masterMode
        });
        this.$el.html(this.template(templateValues));

        // display the feature's field->value table
        this.toggleDefaultsBtn = this.$('.toggle-defaults');
        this.renderAttributes();

        this.delegateEvents(); //not sure I this is necessary but click handler for less/more buttons would only work first time without it

        return myw.translate('details', this.$el);
    },

    /**
     * Display the feature details.
     * Generates the html with a line for each field.
     * If containers information is available it will group the fields with that information
     */
    renderAttributes() {
        this.hasNullOrDefaultValues = false; //USed to determine if this feature has null or default values in any of its attributes
        const feature = this.feature,
            fieldGroups = feature.getFieldGroups(),
            container = this.$('#attributes-detail');

        if (fieldGroups) {
            this.renderFieldGroups(container, fieldGroups);
        } else {
            //no containers definitions for this feature,
            //render as an attribute list
            this.renderAttributeListWithPanel(container);
        }
        if (this.state.collapsed) this.closeDetails();
    },

    /**
     * Displays the feature's attributes as a single table
     * @param  {jQueryElement} container Element where the attributes should be displayed
     */
    renderAttributeListWithPanel(container) {
        //load the HTML for displaying an attribute list
        container.html(this.attributeListTemplate(this.feature.properties));

        const tableBody = container.children('table').children('tbody'),
            renderAll = this.state.attributeDisplayMode == 'full';
        const numAttributesDisplayed = this.renderAttributeList(this.feature, tableBody, renderAll);

        //Update less/more buttons
        this._updateToggleDefaultsBtn(numAttributesDisplayed);
    },

    _updateToggleDefaultsBtn(numAttributesDisplayed) {
        const attMode = this.state.attributeDisplayMode;
        const numDisplayableAttributes = this.feature.getFieldsOrder().length;
        //Update less/more buttons
        if (numAttributesDisplayed == numDisplayableAttributes && !this.hasNullOrDefaultValues) {
            //If all the attributes are being displayed and none of them have null/default values
            //There is no scope for changing the mode to display lesser attributes, so hide the buttons
            this.toggleDefaultsBtn.hide();
        } else if (numAttributesDisplayed) {
            this.toggleDefaultsBtn.show();

            const showShowDefaultsBtn =
                    attMode != 'full' && numAttributesDisplayed <= numDisplayableAttributes,
                showHideDefaultsBtn = attMode == 'full';

            if (showHideDefaultsBtn) {
                //show the '-' button
                this._updateToggleBtnStateTo(true);
            } else if (showShowDefaultsBtn) {
                //show the '+' button
                this._updateToggleBtnStateTo(false);
            }
        }
    },

    _updateToggleBtnStateTo(renderAll) {
        if (renderAll) {
            //show the '-' button
            this.toggleDefaultsBtn.addClass('collapse').attr('title', this.msg('hide-defaults'));
        } else {
            //show the '+' button
            this.toggleDefaultsBtn.removeClass('collapse').attr('title', this.msg('show-all'));
        }
    },

    /**
     * Renders a readonly list displaying the properties of a feature
     * @param  {Feature}    feature             Since this method is accessed from other classes as well
     * @param  {jQueryElement}  tableBodyEl         Element where row elements will be added
     * @param  {boolean}        [renderAll=false]   Whether null and default values should be rendered
     */
    renderAttributeList(feature, tableBodyEl, renderAll) {
        const fieldsOrder = feature.getFieldsOrder();
        let numAttributesDisplayed = 0;

        fieldsOrder.forEach(fieldName => {
            const fieldDD = feature.getFieldDD(fieldName),
                rowElement = this.createRowElement(feature, fieldDD, { renderAll: renderAll });

            if (!this.hasNullOrDefaultValues)
                this.hasNullOrDefaultValues = this._hasNullOrDefaultValue(feature, fieldDD);

            if (rowElement) {
                tableBodyEl.append(rowElement);
                numAttributesDisplayed += 1;
            }
        });
        return numAttributesDisplayed;
    },

    /**
     * Generates the html to display a features' details using containers information
     * @param  {Object} containers The containers information (field groups) of the feature to display
     */
    renderFieldGroups(container, fieldGroups) {
        if (!this.state.groupDisplayMode) this.state.groupDisplayMode = {};
        const self = this;
        const feature = this.feature;
        const panels = $('<div></div>');
        const numGroups = fieldGroups.length;
        let header;
        let table;
        const renderAll = this.state.attributeDisplayMode == 'full';

        // Render each container.
        _.each(fieldGroups, fieldGroup => {
            const containerExternalName = fieldGroup.display_name,
                containerInternalName = self.getSafeIdFrom(containerExternalName);

            if (numGroups > 1) {
                // Setup the top of the panel
                header = $(
                    `<div id="${containerInternalName}" class="container-title detailHeader noselect"><img class = "toggleImage" id="toggle-${containerInternalName}" src="images/collapsed.svg" align="left">${containerExternalName}</div>`
                ).click(function() {
                    self._accordion($(this).attr('id'));
                    return false;
                });
            }
            table = $(
                `<table class="tbl-details left-panel-centered details-container" id="contents-${containerInternalName}" style="display:none"></table>`
            );
            // Populate the panel with its fields.
            _.each(fieldGroup.fields, fieldGroupItem => {
                const fieldInternalName = fieldGroupItem.field_name,
                    fieldDD = feature.getFieldDD(fieldInternalName);
                table.append(self.createRowElement(feature, fieldDD, { renderAll: renderAll }));

                if (!this.hasNullOrDefaultValues)
                    this.hasNullOrDefaultValues = this._hasNullOrDefaultValue(feature, fieldDD);
            });

            //Create a table with the available header + table and add it to the group
            $('<div class="feature-group-container bottom-separator"></div>')
                .append(header)
                .append(table)
                .appendTo(panels);
        });

        container.html(panels);

        // Expand some of the containers.
        _.each(
            fieldGroups,
            function(aFieldGroup) {
                const safeId = this.getSafeIdFrom(aFieldGroup.display_name);
                // If the container is to be expanded, then accordion it.
                if (
                    (aFieldGroup.is_expanded &&
                        this.state.groupDisplayMode[safeId] !== 'collapsed') ||
                    this.state.groupDisplayMode[safeId] === 'expanded'
                ) {
                    this._accordion(safeId);
                }
            },
            this
        );

        if (!this.hasNullOrDefaultValues) {
            this.toggleDefaultsBtn.hide();
        } else {
            this._updateToggleBtnStateTo(renderAll);
        }
    },

    _hasNullOrDefaultValue(feature, fieldDD) {
        const fieldValue = feature.properties[fieldDD.internal_name],
            isDefault = fieldDD['default'] && fieldValue == fieldDD['default'];

        return fieldValue === null || fieldValue === '' || isDefault;
    },

    /**
     * Creates a row element with the label and value of a field
     * @param  {Feature}        feature
     * @param  {fieldDD}            fieldDD
     * @param  {fieldViewerOptions} options     Options to use by the field viewer that will render the attribute
     * @return {jQueryElement}         The newly created tr element (jQuery wrapped)
     */
    createRowElement(feature, fieldDD, options) {
        const rowElement = $('<tr>', { class: 'atts' });
        const fieldNameCell = $(`<th>${fieldDD.external_name}</th>`);
        const valueElement = this.createFieldValueElement(feature, fieldDD, options);

        if (!valueElement.html()) return undefined;

        const isHtml = /<[a-z][\s\S]*>/i;
        const rowContents = $('<td>').append(valueElement);

        if (
            feature.datasource.type === 'kml' &&
            fieldDD.internal_name === 'description' &&
            isHtml.test(valueElement.html())
        ) {
            rowContents.attr('colspan', 2);
        } else {
            rowElement.append(fieldNameCell);
        }
        rowElement.append(rowContents);
        return rowElement;
    },

    /**
     * Creates a Dom element to render a field's value. <br/>
     * The type of element created will depend on the field's dd information
     * @param  {Feature}        feature
     * @param  {fieldDD}            fieldDD
     * @param  {fieldViewerOptions} options     Options to use by the field viewer that will render the attribute
     * @return {jQueryElement}         The newly created element (jQuery wrapped)
     */
    createFieldValueElement(feature, fieldDD, options) {
        const fieldViewer = this.getFieldViewer(feature, fieldDD, options);

        return fieldViewer.$el;
    },

    /**
     * Creates an instance of an field viewer class that is appropriate to render a field's value
     * @param  {Feature}        feature
     * @param  {fieldDD}            fieldDD
     * @param  {fieldViewerOptions} options     Options to use by the field viewer
     * @return {FieldViewer}
     */
    getFieldViewer(feature, fieldDD, options) {
        /* jshint newcap: false */
        let viewerClass;

        let fieldType = fieldDD.type;
        const bracketIndex = fieldType.indexOf('(');
        let customFieldViewer;

        //strip parentesis section from field type
        if (bracketIndex > 0) {
            fieldType = fieldType.slice(0, bracketIndex);
        }
        //check if there is a specific field viewer defined for this feature&field
        customFieldViewer = feature.getCustomFieldViewerFor(fieldDD);
        if (customFieldViewer) {
            viewerClass = customFieldViewer;
        } else {
            const classOrFunction = this.fieldViewerMapping[fieldType];
            if (typeof classOrFunction == 'function' && classOrFunction.hasOwnProperty('extend')) {
                //class
                viewerClass = classOrFunction;
            } else if (typeof classOrFunction == 'function') {
                viewerClass = classOrFunction(fieldDD);
            }

            if (!viewerClass) {
                viewerClass = this.fieldViewerMapping['default'];
            }
        }
        return new viewerClass(this, feature, fieldDD, options);
    },

    /**
     * Toggle the subject element in the left area.
     * @private
     */
    _accordion(subject) {
        $(`#contents-${subject}`).toggle();
        $(`#footer-${subject}`).toggle();
        const toggleAnchor = document.getElementById(`toggle-${subject}`);
        if (!toggleAnchor) return;
        if (toggleAnchor.src.indexOf('expanded.svg') > 0) {
            toggleAnchor.src = 'images/collapsed.svg';
            this.state.groupDisplayMode[subject] = 'collapsed';
        } else {
            toggleAnchor.src = 'images/expanded.svg';
            this.state.groupDisplayMode[subject] = 'expanded';
        }
    },
    //Handles the expand and collapse actions initiated by clicking on the panel-header
    toggleDetails() {
        this.state.collapsed = !this.state.collapsed;
        this.$('#attributes-detail').toggle();
        this.$('.feature-plugins-header, .panel-header').toggleClass('collapsed');
    },
    closeDetails() {
        this.$('#attributes-detail').hide();
        this.$('.feature-plugins-header, .panel-header').toggleClass('collapsed', true);
    }
});

export default myw.FeatureViewer;
